import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../components/common/PageHeader';
import { feedbackService } from '../../services';
import { formatDate } from '../../utils/formatters';

const statusOptions = ['NEW', 'IN_PROGRESS', 'RESOLVED', 'REJECTED'];

const FeedbackManagement = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [updatingId, setUpdatingId] = useState(null);

  const [filters, setFilters] = useState({
    userId: '',
    status: '',
  });

  const fetchFeedback = async () => {
    try {
      setLoading(true);
      setError('');
      const resp = await feedbackService.getAdminFeedback({
        userId: filters.userId || undefined,
        status: filters.status || undefined,
        sortBy: 'createdAt',
        order: 'DESC',
        limit: 50,
        offset: 0,
      });
      const list = resp?.data?.items || resp?.items || resp?.data || resp || [];
      setItems(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e.response?.data?.message || t('feedback.admin.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpdateStatus = async (id, status) => {
    setUpdatingId(id);
    setError('');
    setSuccess('');
    try {
      await feedbackService.updateFeedbackStatus(id, status);
      setItems((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
      setSuccess(t('feedback.admin.updateSuccess'));
      setTimeout(() => setSuccess(''), 2500);
    } catch (e) {
      setError(e.response?.data?.message || t('feedback.admin.updateError'));
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-primary-50 pb-12">
      <PageHeader
        title={t('feedback.admin.title')}
        subtitle={t('feedback.admin.subtitle')}
        icon={
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5h6m-6 4h6m-7 4h8m-9 8h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        }
      />

      <div className="max-w-7xl mx-auto px-4">
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg text-sm">
            {success}
          </div>
        )}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">{t('feedback.admin.userId')}</label>
              <input
                value={filters.userId}
                onChange={(e) => setFilters((p) => ({ ...p, userId: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder={t('feedback.admin.userIdPlaceholder')}
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">{t('common.status')}</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
              >
                <option value="">{t('common.all')}</option>
                {statusOptions.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={fetchFeedback}
                className="px-4 py-2 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700"
              >
                {t('actions.refresh')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setFilters({ userId: '', status: '' });
                  setTimeout(() => fetchFeedback(), 0);
                }}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold"
              >
                {t('actions.clear')}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="p-10 flex justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
            </div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-gray-600">{t('feedback.admin.empty')}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-100 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{t('common.date')}</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{t('feedback.admin.user')}</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{t('feedback.subject')}</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{t('common.details')}</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{t('common.status')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {items.map((fb) => (
                    <tr key={fb.id} className="hover:bg-primary-50/40">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{formatDate(fb.createdAt, lang)}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <div className="font-semibold text-gray-900">{fb.user?.fullName || fb.name || '—'}</div>
                        <div className="text-xs text-gray-500">{fb.user?.email || fb.email || ''}</div>
                        <div className="text-[11px] text-gray-400">{fb.userId || fb.user?.id || ''}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 font-semibold break-words">{fb.subject || '—'}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 whitespace-pre-wrap break-words max-w-xl">{fb.message}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <select
                          value={fb.status || 'NEW'}
                          disabled={updatingId === fb.id}
                          onChange={(e) => handleUpdateStatus(fb.id, e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg bg-white"
                        >
                          {statusOptions.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackManagement;

